import { useMemo, useState } from 'react';
import {
  AUTH_STORES_REQUEST_OTP,
  AUTH_STORES_VERIFY_OTP,
  AUTH_STORES_RESET_PASSWORD
} from '../../../shared/constants/endpoints';
import useApiRequest from '../../../shared/hooks/useApiRequest';
import { logger } from '../../../shared/utils/logger.js';

const LOG_MODULE = 'useForgotPassword';

const useForgotPassword = ({ request, onToast, runWithGlobalLoader }) => {
  const [step, setStep] = useState('idle');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [resetToken, setResetToken] = useState('');

  const { execute: executeRequestOtp, loading: requestLoading } = useApiRequest();
  const { execute: executeVerifyOtp, loading: verifyLoading } = useApiRequest();
  const { execute: executeResetPassword, loading: resetLoading } = useApiRequest();

  const loading = useMemo(() => requestLoading || verifyLoading || resetLoading, [requestLoading, resetLoading, verifyLoading]);

  const transition = (nextStep) => {
    logger.info(LOG_MODULE, 'Forgot password step transition', {
      from: step,
      to: nextStep
    });
    setStep(nextStep);
  };

  const reset = () => {
    setStep('idle');
    setEmail('');
    setOtp('');
    setNewPassword('');
    setConfirmPassword('');
    setMaskedEmail('');
    setResetToken('');
  };

  const requestOtp = async () => {
    const storeCode = String(email || '').trim();
    if (!storeCode) {
      onToast?.({ message: 'Store code is required', variant: 'error' });
      return false;
    }

    try {
      const response = await executeRequestOtp(
        () => runWithGlobalLoader(() => request(AUTH_STORES_REQUEST_OTP, {
          method: 'POST',
          body: JSON.stringify({ storeCode }),
          requestName: 'forgot-password-request-otp'
        })),
        { logModule: 'forgot-password.request-otp' }
      );

      const nextMaskedEmail = response?.data?.maskedEmail || '';
      setMaskedEmail(nextMaskedEmail);
      transition('requested');
      onToast?.({ message: `OTP sent${nextMaskedEmail ? ` to ${nextMaskedEmail}` : ''}`, variant: 'success' });
      return true;
    } catch (error) {
      const message = error?.data?.message || error?.message || 'Failed to send OTP';
      onToast?.({ message, variant: 'error' });
      return false;
    }
  };

  const verifyOtp = async () => {
    const storeCode = String(email || '').trim();
    const otpValue = String(otp || '').trim();

    if (!storeCode) {
      onToast?.({ message: 'Store code is required', variant: 'error' });
      return false;
    }
    if (!/^\d{6}$/.test(otpValue)) {
      onToast?.({ message: 'OTP must be a 6-digit number', variant: 'error' });
      return false;
    }

    try {
      const response = await executeVerifyOtp(
        () => runWithGlobalLoader(() => request(AUTH_STORES_VERIFY_OTP, {
          method: 'POST',
          body: JSON.stringify({ storeCode, otp: otpValue }),
          requestName: 'forgot-password-verify-otp'
        })),
        { logModule: 'forgot-password.verify-otp' }
      );

      const token = response?.data?.resetToken || '';
      if (!token) {
        onToast?.({ message: 'Failed to verify OTP', variant: 'error' });
        return false;
      }

      setResetToken(token);
      transition('verified');
      onToast?.({ message: 'OTP verified. Set your new password.', variant: 'success' });
      return true;
    } catch (error) {
      const message = error?.data?.message || error?.message || 'Failed to verify OTP';
      onToast?.({ message, variant: 'error' });
      return false;
    }
  };

  const resetPassword = async () => {
    if (!resetToken) {
      onToast?.({ message: 'Reset token not found. Please verify OTP again.', variant: 'error' });
      transition('requested');
      return false;
    }

    if (!newPassword || newPassword.length < 8) {
      onToast?.({ message: 'Password must be at least 8 characters', variant: 'error' });
      return false;
    }
    if (!/[A-Z]/.test(newPassword)) {
      onToast?.({ message: 'Password must contain at least one capital letter', variant: 'error' });
      return false;
    }
    if (!/[a-zA-Z]/.test(newPassword) || !/[0-9]/.test(newPassword)) {
      onToast?.({ message: 'Password must be alphanumeric', variant: 'error' });
      return false;
    }
    if (newPassword !== confirmPassword) {
      onToast?.({ message: 'Passwords do not match', variant: 'error' });
      return false;
    }

    try {
      await executeResetPassword(
        () => runWithGlobalLoader(() => request(AUTH_STORES_RESET_PASSWORD, {
          method: 'POST',
          body: JSON.stringify({ resetToken, newPassword }),
          requestName: 'forgot-password-reset'
        })),
        { logModule: 'forgot-password.reset' }
      );

      transition('reset');
      onToast?.({ message: 'Password reset successful! You can now log in.', variant: 'success' });
      return true;
    } catch (error) {
      const message = error?.data?.message || error?.message || 'Failed to reset password';
      onToast?.({ message, variant: 'error' });
      return false;
    }
  };

  return {
    step,
    email,
    otp,
    newPassword,
    confirmPassword,
    maskedEmail,
    loading,
    setEmail,
    setOtp,
    setNewPassword,
    setConfirmPassword,
    requestOtp,
    verifyOtp,
    resetPassword,
    reset
  };
};

export default useForgotPassword;
